import { create } from 'zustand';

import { auth as authApi, meta as metaApi, setCsrfToken, setUnauthorizedHandler } from '../lib/api.js';

export const useAuthStore = create((set, get) => ({
  user: null,
  bootstrap: null,
  status: 'loading', // loading | anonymous | authenticated
  error: null,

  async init() {
    try {
      const { user, csrf_token } = await authApi.me();
      setCsrfToken(csrf_token);
      set({ user, status: 'authenticated', error: null });
      // Nothing else is reachable until the password is changed, so don't fetch it.
      if (!user.must_change_password) get().loadBootstrap();
    } catch {
      set({ user: null, status: 'anonymous' });
    }
  },

  async loadBootstrap() {
    try {
      set({ bootstrap: await metaApi.bootstrap() });
    } catch {
      /* non-fatal: the UI falls back to its built-in vocabulary */
    }
  },

  async login(username, password) {
    set({ error: null });
    const { user, csrf_token } = await authApi.login(username, password);
    setCsrfToken(csrf_token);
    set({ user, status: 'authenticated' });
    if (!user.must_change_password) get().loadBootstrap();
    return user;
  },

  async logout() {
    try {
      await authApi.logout();
    } finally {
      setCsrfToken(null);
      set({ user: null, bootstrap: null, status: 'anonymous' });
    }
  },

  async changePassword(currentPassword, newPassword) {
    await authApi.changePassword(currentPassword, newPassword);
    set((s) => ({ user: { ...s.user, must_change_password: false } }));
    get().loadBootstrap();
  },

  isAdmin: () => get().user?.role === 'admin',
}));

// A 401 anywhere means the session is gone; drop straight to the login screen rather
// than letting the UI show empty panels.
setUnauthorizedHandler(() => {
  setCsrfToken(null);
  useAuthStore.setState({ user: null, bootstrap: null, status: 'anonymous' });
});
